import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { UploadFile, Download, Delete, Image } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

const ACCEPT = {
  all: '*',
  docs: '.pdf,.doc,.docx,.xls,.xlsx,.csv',
  images: '.png,.jpg,.jpeg,.svg',
};

const Documents = () => {
  const history = useHistory();
  const inputRef = useRef(null);
  const [meta, setMeta] = useState({
    projectName: 'GreenVille - Rooftop A1',
    projectRef: 'PRJ-10231',
    stage: '',
    type: 'Document',
    tags: '',
  });

  const stages = useMemo(() => ([
    'Lead Generation',
    'Site Survey',
    'Quotation',
    'Order Confirmed',
    'Installation Planning',
    'Installation',
    'Quality Check',
    'Commissioning',
    'Handover',
    'AMC / Maintenance',
  ]), []);

  const [files, setFiles] = useState([
    { id: 1, name: 'site-survey.pdf', size: 245_000, type: 'Document', stage: 'Site Survey', tags: 'survey,roof', date: '2025-10-28' },
    { id: 2, name: 'roof-image.jpg', size: 560_000, type: 'Image', stage: 'Installation Planning', tags: 'rooftop', date: '2025-10-30', preview: true },
  ]);
  const [addOpen, setAddOpen] = useState(false);

  const formattedSize = (s) => {
    if (s >= 1024 * 1024) return `${(s / (1024 * 1024)).toFixed(1)} MB`;
    if (s >= 1024) return `${(s / 1024).toFixed(1)} KB`;
    return `${s} B`;
  };

  const onBrowse = () => inputRef.current?.click();

  const onUpload = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(prev => ([
      ...prev,
      ...list.map(f => ({
        id: Date.now() + Math.random(),
        name: f.name,
        size: f.size,
        type: meta.type,
        stage: meta.stage || 'General',
        tags: meta.tags,
        date: new Date().toISOString().split('T')[0],
        preview: f.type.startsWith('image/'),
      }))
    ]));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const list = Array.from(e.dataTransfer.files || []);
    setFiles(prev => ([
      ...prev,
      ...list.map(f => ({
        id: Date.now() + Math.random(),
        name: f.name,
        size: f.size,
        type: meta.type,
        stage: meta.stage || 'General',
        tags: meta.tags,
        date: new Date().toISOString().split('T')[0],
        preview: f.type.startsWith('image/'),
      }))
    ]));
  };

  const remove = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const filtered = files.filter(f => !meta.stage || f.stage === meta.stage);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Attach Documents / Images
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload survey docs, drawings, installation photos, and QC/commissioning records
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<UploadFile />} onClick={() => history.push('/app/green-energy/project/attachments/add')} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>
            Add Attachment
          </Button>
        </Box>
      </Box>

      {/* Project Summary & Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField size="small" fullWidth label="Project Name" value={meta.projectName} onChange={(e) => setMeta(prev => ({ ...prev, projectName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField size="small" fullWidth label="Project Reference" value={meta.projectRef} onChange={(e) => setMeta(prev => ({ ...prev, projectRef: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Stage</InputLabel>
                <Select value={meta.stage} label="Stage" onChange={(e) => setMeta(prev => ({ ...prev, stage: e.target.value }))}>
                  <MenuItem value="">All Stages</MenuItem>
                  {stages.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={meta.type} label="Type" onChange={(e) => setMeta(prev => ({ ...prev, type: e.target.value }))}>
                  <MenuItem value="Document">Document</MenuItem>
                  <MenuItem value="Image">Image</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Add Attachment handled on dedicated page */}

      {/* Preview Grid for images */}
      {/* <Grid container spacing={2} sx={{ mb: 2 }}>
        {filtered.filter(f => f.preview).slice(0, 6).map(f => (
          <Grid item xs={6} md={2} key={f.id}>
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Box sx={{ height: 90, backgroundColor: '#eee', borderRadius: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption">Image Preview</Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>{f.name}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid> */}

      {/* Files Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Files</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Stage</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Tags</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Size</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(f => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{f.name}</TableCell>
                    <TableCell>{f.type}</TableCell>
                    <TableCell>
                      <Chip size="small" label={f.stage} color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {f.tags?.split(',').filter(Boolean).map(t => (
                          <Chip key={`${f.id}-${t}`} size="small" label={t.trim()} variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>{formattedSize(f.size)}</TableCell>
                    <TableCell>{f.date}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Download">
                        <IconButton size="small"><Download fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => remove(f.id)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Documents;


